import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { usePageSeo } from "@/lib/usePageSeo";
import { Reveal } from "@/components/util/Reveal";

/**
 * About page hero — cinematic full-width media with independent
 * desktop and mobile media pipelines. Owner controls every axis of
 * both hero viewports from the Dashboard:
 *
 *   about_hero_desktop_media_type ∈ {"image", "video"}
 *   about_image                    → desktop image  (+ video poster)
 *   about_hero_desktop_video_mp4   → desktop video (MP4 required)
 *   about_hero_desktop_video_webm  → desktop video (optional WebM)
 *   about_hero_desktop_focal       → 9-position focal token
 *
 *   about_hero_mobile_media_type   ∈ {"image", "video"}
 *   about_image_mobile             → mobile image (+ video poster)
 *   about_hero_mobile_video_mp4    → mobile video (MP4 required)
 *   about_hero_mobile_video_webm   → mobile video (optional WebM)
 *   about_hero_mobile_focal        → 9-position focal token
 *
 * The About hero NEVER falls back to Homepage hero media. If a mobile
 * asset is intentionally blank the ONLY allowed About-scoped fallback
 * is `about_image` (the desktop image), never `hero_image_*`.
 *
 * Video mode paints from first frame — the static image is used only
 * as the `<video poster>` so the video element itself is what renders
 * from t=0. There is no image → video swap.
 */

// Nine-position focal tokens → CSS `object-position` values. Kept as a
// small helper so both `<img>` and `<video>` use exactly the same
// mapping and the admin dropdown values map 1:1 to what renders.
const FOCAL_POSITIONS = {
  "top-left":      "15% 15%",
  "top":           "50% 15%",
  "top-right":     "85% 15%",
  "left":          "15% 50%",
  "center":        "50% 50%",
  "right":         "65% 50%",
  "bottom-left":   "15% 85%",
  "bottom":        "50% 85%",
  "bottom-right":  "85% 85%",
};
const focal = (token) => FOCAL_POSITIONS[token] || FOCAL_POSITIONS.center;

export default function AboutPage() {
  const s = useSettings();

  usePageSeo({
    title: s.about_heading_line1
      ? `${s.about_heading_line1} ${s.about_heading_line2 || ""} — Ur Designs`.trim()
      : "About Ur Designs",
    description: s.about_meta_description || s.about_paragraph,
    path: "/about",
    image: s.about_image || undefined,
  });

  const eyebrow = s.about_eyebrow;
  const heading1 = s.about_heading_line1;
  const heading2 = s.about_heading_line2;
  const paragraph = s.about_paragraph;
  const imgAlt = s.about_hero_video_alt;
  const ctaLabel = s.about_cta_label;
  const ctaLink = s.about_cta_link;
  const accentInline = !!s.about_accent_inline;

  // Founder story content (Sep 2026) — paragraphs are blank-line separated
  // in CMS textareas.
  const splitParas = (t) => (t || "").split(/\n\s*\n/).map((x) => x.trim()).filter(Boolean);
  const heroParas = splitParas(paragraph);
  const storyParas = splitParas(s.about_story_body);
  const nextChapterParas = splitParas(s.about_next_chapter_body);
  const stillBuildingParas = splitParas(s.about_still_building_body);
  const pillars = [
    { key: "create", label: "CREATE", text: s.about_pillar_create },
    { key: "equip", label: "EQUIP", text: s.about_pillar_equip },
    { key: "power", label: "POWER", text: s.about_pillar_power },
  ];
  // Website Repositioning (Sep 2026) — the CREATE/EQUIP/POWER pillars
  // promote the workstation + software business lines, so they only
  // render while public commerce is switched ON in the CMS. The pillar
  // copy, CMS fields and layout are preserved untouched for reactivation.
  const showPillars = s.public_commerce_workstations_software_enabled === true;

  // Desktop media state — fully independent from mobile.
  const desktopType = String(s.about_hero_desktop_media_type || "image").toLowerCase();
  const desktopImg = s.about_image || "";
  const desktopVideoMp4 = s.about_hero_desktop_video_mp4 || "";
  const desktopVideoWebm = s.about_hero_desktop_video_webm || "";
  const desktopFocal = focal(s.about_hero_desktop_focal || "top-right");

  // Mobile media state — fully independent from desktop.
  const mobileType = String(s.about_hero_mobile_media_type || "image").toLowerCase();
  // Only About-scoped fallback: mobile falls back to the About DESKTOP
  // image if the mobile image is intentionally blank. Never Homepage.
  const mobileImg = s.about_image_mobile || s.about_image || "";
  const mobileVideoMp4 = s.about_hero_mobile_video_mp4 || "";
  const mobileVideoWebm = s.about_hero_mobile_video_webm || "";
  const mobileFocal = focal(s.about_hero_mobile_focal || "top-right");

  // Viewport gate — lazy initializer reads `matchMedia` synchronously
  // on the first render so the initial DOM already reflects the
  // correct branch, preventing a mobile <video> from briefly mounting
  // on desktop (or vice versa) before an effect runs.
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const apply = () => setIsDesktop(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener("change", apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", apply) : mq.removeListener(apply);
    };
  }, []);

  // Reduced motion — lazy initializer stops a brief <video> mount for
  // visitors who explicitly opted out of motion.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setPrefersReducedMotion(mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener("change", apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", apply) : mq.removeListener(apply);
    };
  }, []);

  // Playback-error escape: a genuine <video> onError is the only reason
  // we fall back to the About image. Load-path swapping is never used.
  const [desktopVideoBroken, setDesktopVideoBroken] = useState(false);
  const [mobileVideoBroken, setMobileVideoBroken] = useState(false);
  useEffect(() => { setDesktopVideoBroken(false); }, [desktopVideoMp4, desktopVideoWebm]);
  useEffect(() => { setMobileVideoBroken(false); }, [mobileVideoMp4, mobileVideoWebm]);

  const wantsDesktopVideo =
    desktopType === "video" &&
    !!desktopVideoMp4 &&
    !desktopVideoBroken &&
    !prefersReducedMotion;
  const wantsMobileVideo =
    mobileType === "video" &&
    !!mobileVideoMp4 &&
    !mobileVideoBroken &&
    !prefersReducedMotion;

  const showDesktopVideo = isDesktop && wantsDesktopVideo;
  const showMobileVideo = !isDesktop && wantsMobileVideo;

  const desktopMediaMode = wantsDesktopVideo ? "video" : "image";
  const mobileMediaMode = wantsMobileVideo ? "video" : "image";

  return (
    <div data-testid="about-page" className="bg-[#0F0F0F]">
      <section
        data-testid="about-hero"
        data-media-mode={isDesktop ? desktopMediaMode : mobileMediaMode}
        data-desktop-media-mode={desktopMediaMode}
        data-mobile-media-mode={mobileMediaMode}
        className="relative bg-[#0F0F0F] overflow-hidden"
      >
        {/* ================================================== */}
        {/* MOBILE — media banner on top, content block below.  */}
        {/* Independent from desktop. Falls back only to the    */}
        {/* About desktop image when the mobile image field is  */}
        {/* blank — never to Homepage hero media.               */}
        {/* ================================================== */}
        <div className="lg:hidden">
          {(showMobileVideo || mobileImg) && (
            <div data-testid="about-hero-mobile-media" className="relative w-full">
              {showMobileVideo ? (
                <video
                  data-testid="about-hero-mobile-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  poster={mobileImg || undefined}
                  aria-label={imgAlt}
                  onError={() => setMobileVideoBroken(true)}
                  className="block w-full h-auto object-cover bg-[#0F0F0F]"
                  style={{ aspectRatio: "16/11", objectPosition: mobileFocal }}
                >
                  {mobileVideoWebm && <source src={mobileVideoWebm} type="video/webm" />}
                  <source src={mobileVideoMp4} type="video/mp4" />
                </video>
              ) : (
                <img
                  data-testid="about-hero-mobile-image"
                  src={mobileImg}
                  alt={imgAlt}
                  className="block w-full h-auto object-cover"
                  style={{ aspectRatio: "16/11", objectPosition: mobileFocal }}
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                />
              )}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-14"
                style={{ background: "linear-gradient(to bottom, transparent 0%, #0F0F0F 100%)" }}
              />
            </div>
          )}
          <div className="bg-[#0F0F0F] px-5 pt-4 pb-14">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="ur-eyebrow"
              data-testid="about-eyebrow"
            >
              {eyebrow}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display mt-4 text-[38px] xs:text-[42px] font-black leading-[1.02] tracking-tight text-white"
              data-testid="about-heading"
            >
              {heading1}
              {accentInline
                ? <span className="text-[#FF6600]">{heading2}</span>
                : <> <span className="text-[#FF6600]">{heading2}</span></>}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.12 }}
              className="mt-5 max-w-md space-y-4"
              data-testid="about-paragraph"
            >
              {heroParas.map((p, i) => (
                <p key={i} className="text-[#B5B5B5] text-[15px] leading-relaxed">{p}</p>
              ))}
            </motion.div>
            <div className="mt-8">
              <Link data-testid="about-cta" to={ctaLink} className="ur-btn-primary">
                {ctaLabel} →
              </Link>
            </div>
          </div>
        </div>

        {/* ================================================== */}
        {/* DESKTOP — content overlays a full-width media layer  */}
        {/* Independent CMS pipeline from mobile.               */}
        {/* ================================================== */}
        <div className="hidden lg:block relative">
          {/* Ambient orange glow behind the composition — same
              cinematic language as the Home hero. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-24 right-0 w-[640px] h-[640px] rounded-full opacity-[0.18] blur-[140px]"
            style={{ background: "radial-gradient(circle, #FF8A33 0%, transparent 60%)" }}
          />

          <div className="relative min-h-[480px] lg:min-h-[500px] xl:min-h-[540px] flex items-center">
            {/* Media layer — the image / video is height-fitted at
                its natural aspect ratio and anchored to the right
                edge so the person renders at a modest ~200 px width
                instead of the ~480 px cover-crop scale-up. The
                container's `#0F0F0F` background fills the left band
                behind the copy; the readability gradient below is
                smooth enough that the image's left edge feathers
                into the dark section without a visible seam.
                Result: full-width dark cinematic hero, studio /
                workstation dominant, person present but secondary. */}
            <div
              data-testid="about-hero-desktop-media"
              className="absolute inset-0 overflow-hidden bg-[#0F0F0F]"
            >
              {showDesktopVideo ? (
                <video
                  data-testid="about-hero-desktop-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  poster={desktopImg || undefined}
                  aria-label={imgAlt}
                  onError={() => setDesktopVideoBroken(true)}
                  className="absolute top-0 right-0 h-full w-auto object-cover"
                  style={{ objectPosition: desktopFocal }}
                >
                  {desktopVideoWebm && <source src={desktopVideoWebm} type="video/webm" />}
                  <source src={desktopVideoMp4} type="video/mp4" />
                </video>
              ) : (
                <img
                  data-testid="about-hero-desktop-image"
                  src={desktopImg}
                  alt={imgAlt}
                  className="absolute top-0 right-0 h-full w-auto object-cover"
                  style={{ objectPosition: desktopFocal }}
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                />
              )}
              {/* Smooth left-to-right readability gradient — starts
                  100 % opaque on the far left, is STILL at ~55 %
                  opacity at 55 % of the hero width (i.e. right where
                  the height-fitted image begins), then fades to fully
                  transparent by 92 %. That's the whole trick that
                  hides the image's left edge without creating a
                  visible column seam. Image and Video modes share
                  the exact same overlay. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to right, #0F0F0F 0%, rgba(15,15,15,0.95) 30%, rgba(15,15,15,0.6) 55%, rgba(15,15,15,0.2) 75%, transparent 92%)",
                }}
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-20"
                style={{ background: "linear-gradient(to bottom, transparent 0%, #0F0F0F 100%)" }}
              />
            </div>

            {/* Content — overlays the media on the left half. */}
            <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
              <div className="flex flex-col py-10 lg:py-12 max-w-[640px]">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="ur-eyebrow"
                  data-testid="about-eyebrow-desktop"
                >
                  {eyebrow}
                </motion.div>
                <motion.h1
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.05 }}
                  className="font-display mt-5 text-5xl xl:text-6xl font-black leading-[1.02] tracking-tight text-white"
                  data-testid="about-heading-desktop"
                >
                  {heading1}
                  {accentInline
                    ? <span className="text-[#FF6600]">{heading2}</span>
                    : <> <span className="text-[#FF6600]">{heading2}</span></>}
                </motion.h1>
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.12 }}
                  className="mt-6 max-w-xl space-y-4"
                  data-testid="about-paragraph-desktop"
                >
                  {heroParas.map((p, i) => (
                    <p key={i} className="text-[#B5B5B5] text-lg leading-relaxed">{p}</p>
                  ))}
                </motion.div>
                <div className="mt-8">
                  <Link data-testid="about-cta-desktop" to={ctaLink} className="ur-btn-primary">
                    {ctaLabel} →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Story ──────────────────────────────────────────────────── */}
      {storyParas.length > 0 && (
        <section data-testid="about-story" className="relative bg-[#0F0F0F] px-5 sm:px-6 lg:px-10 py-14 lg:py-20 border-t border-white/5">
          <div className="max-w-[840px] mx-auto space-y-7">
            {storyParas.map((p, i) => (
              <p
                key={i}
                data-testid={i === storyParas.length - 1 ? "about-story-belief" : undefined}
                className={
                  i === storyParas.length - 1
                    ? "font-display text-xl sm:text-2xl font-bold text-white leading-snug"
                    : "text-base lg:text-lg text-[#B5B5B5] leading-relaxed"
                }
              >
                {p}
              </p>
            ))}
          </div>
        </section>
      )}

      {/* ── The Next Chapter + CREATE / EQUIP / POWER ────────────────── */}
      <section data-testid="about-next-chapter" className="relative bg-[#0F0F0F] px-5 sm:px-6 lg:px-10 py-14 lg:py-20 border-t border-white/5">
        <div className="max-w-[840px] mx-auto">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {s.about_next_chapter_heading}
            </h2>
          </Reveal>
          <div className="mt-8 space-y-6">
            {nextChapterParas.map((p, i) => (
              <p key={i} className="text-base lg:text-lg text-[#B5B5B5] leading-relaxed">{p}</p>
            ))}
          </div>
        </div>

        <div className="max-w-[1120px] mx-auto">
          {showPillars && (
          <div data-testid="about-pillars" className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-14">
            {pillars.map((pl) => (
              <Reveal key={pl.key}>
                <div data-testid={`about-pillar-${pl.key}`} className="border-l-2 border-[#FF6600] pl-6">
                  <h3 className="font-display text-lg sm:text-xl font-bold uppercase tracking-[0.22em] text-[#FF6600]">
                    {pl.label}
                  </h3>
                  <p className="mt-4 text-base text-[#B5B5B5] leading-relaxed">{pl.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          )}
        </div>

        <div className="max-w-[840px] mx-auto">
          <Reveal>
            <p data-testid="about-tagline" className="mt-16 font-display text-2xl sm:text-3xl font-bold text-white leading-snug">
              {s.about_tagline}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Still Building + founder signature ───────────────────────── */}
      <section data-testid="about-still-building" className="relative bg-[#0F0F0F] px-5 sm:px-6 lg:px-10 py-14 lg:py-20 border-t border-white/5">
        <div className="max-w-[840px] mx-auto">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {s.about_still_building_heading}
            </h2>
          </Reveal>
          <div className="mt-8 space-y-6">
            {stillBuildingParas.map((p, i) => (
              <p key={i} className="text-base lg:text-lg text-[#B5B5B5] leading-relaxed">{p}</p>
            ))}
          </div>
          <Reveal>
            <div data-testid="about-signature" className="mt-16 pt-10 border-t border-white/10">
              <p className="font-display text-2xl font-bold text-white">{s.about_founder_name}</p>
              <p className="mt-2 text-sm uppercase tracking-[0.18em] text-[#A3A3A3]">{s.about_founder_role}</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
